/**
 * @file The local storage service
 * @author Heino Lenting
 */
/**
 * Class for the local storage service
 */
var LocalStorageService = /** @class */ (function () {
    /**
     * Constructs a LocalStorageService
     */
    function LocalStorageService() {
    }
    /**
     * Used for reading a property from the storage
     *
     * @param {StorageType} storageType Type of the storage
     * @param {StorageKey} storageKey The key used for storing
     * @param {StorageValue} defaultValue The default value for when the value does not exists
     * @param {StorageValue[]} allowedValues The allowed values
     * @returns {StorageValue} The value
     */
    LocalStorageService.prototype.readProperty = function (storageType, storageKey, defaultValue, allowedValues) {
        if (this.hasStorageSupport()) {
            // Test if default value is allowed
            if (this.isValueAllowed(defaultValue, allowedValues)) {
                // Get the correct storage
                var storage = this.getStorage(storageType);
                // Get the raw value
                var storedValueRaw = storage.getItem(storageKey);
                // If the raw value does not exits, return the default value
                if (storedValueRaw === null || storedValueRaw === undefined) {
                    return defaultValue;
                }
                // Parse the stored raw value
                var storedValue = (storedValueRaw) ? JSON.parse(storedValueRaw) : undefined;
                // If stored value is not valid, delete it and return default
                if (!this.isValueAllowed(storedValue, allowedValues)) {
                    // The stored value is not valid, delete it
                    this.deleteProperty(storageType, storageKey);
                    // So we use the default
                    return defaultValue;
                }
                // Store value is OK, return this one
                return storedValue;
            }
            else {
                console.warn('The default value is not allowed in the storage ("' + defaultValue + '")');
                return undefined;
            }
        }
        else {
            console.warn('Can not read a property because the browser does not support local and/or session storage.');
            return defaultValue;
        }
    };
    /**
     * Used for updating a property from the storage
     * @param {StorageType} storageType Type of the storage
     * @param {StorageKey} storageKey The key used for storing
     * @param {StorageValue} defaultValue The storage value
     * @param {StorageValue[]} allowedValues The allowed values
     * @returns {StorageValue} The stored value
     */
    LocalStorageService.prototype.updateProperty = function (storageType, storageKey, storageValue, allowedValues) {
        if (this.hasStorageSupport()) {
            // Test if default value is allowed
            if (this.isValueAllowed(storageValue, allowedValues)) {
                // Get the correct storage
                var storage = this.getStorage(storageType);
                // Update the property
                storage.setItem(storageKey, JSON.stringify(storageValue));
                // Return the stored value
                return storageValue;
            }
            else {
                console.warn('The value is not allowed in the storage ("' + storageValue + '")');
                return undefined;
            }
        }
        else {
            console.warn('Can not update a property because the browser does not support local and/or session storage.');
            return undefined;
        }
    };
    LocalStorageService.prototype.deleteProperty = function (storageType, storageKey) {
        if (this.hasStorageSupport()) {
            // Get the correct storage
            var storage = this.getStorage(storageType);
            // Remote key from storage
            storage.removeItem(storageKey);
        }
        else {
            console.warn('Can not delete a property because the browser does not support local and/or session storage.');
        }
    };
    /**
     *  Check if browser has support for storage
     * @returns boolean Whether browser has support for storage or not
     */
    LocalStorageService.prototype.hasStorageSupport = function () {
        return (window['localStorage'] !== undefined && window['sessionStorage'] !== undefined);
    };
    /**
     * Get the specified storage
     * @param {StorageType} storageType The stype storage (session or local)
     * @returns {Storage} The storage
     */
    LocalStorageService.prototype.getStorage = function (storageType) {
        if (storageType === 'local') {
            return window.localStorage;
        }
        if (storageType === 'session') {
            return window.sessionStorage;
        }
        console.warn('Unknown storage type');
        return undefined;
    };
    /**
     * Check if the value is allowed
     * @private
     * @param {StorageValue} storageValue The value
     * @param {StorageValue[]} allowedValues The allowed values
     * @returns {boolean}
     */
    LocalStorageService.prototype.isValueAllowed = function (storageValue, allowedValues) {
        if (allowedValues === undefined) {
            // All values are allowed
            return true;
        }
        if (allowedValues.length === 0) {
            // Allowed values are missing
            console.warn('Allowed values array for the local storage is empty');
            return false;
        }
        // Find a match
        var matchedValue = allowedValues.find(function (allowedValue) { return allowedValue === storageValue; });
        if (matchedValue === undefined) {
            console.warn('Storage value "' + storageValue + '" does not exists in the following allowed values "' + allowedValues + '"');
            return false;
        }
        return true;
    };
    return LocalStorageService;
}());
export { LocalStorageService };
